import Sheet from 'components/utils/Sheet'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Typography from 'LuxKit/Typography/Typography'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { useAppSelector } from 'hooks/reduxHooks'
import { rem } from 'polished'
import { useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import LoyaltyLandingPageLargeTile from './LoyaltyLandingPageLargeTile'
import LoyaltyLandingPageSmallTile from './LoyaltyLandingPageSmallTile'
import useLoyaltyLandingPageBenefitCopy from 'loyalty/hooks/useLoyaltyLandingPageBenefitCopy'
import { EmptyArray } from 'lib/array/arrayUtils'
import LayoutContainer from 'components/Common/LayoutContainer'

const SmallTilesSection = styled.div`
  display: grid;
  gap: ${rem(24)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(3, 1fr);
  }
`

interface Props {
  onActivate?: () => void
}

function LoyaltyUserPlatinumBenefits(props: Props) {
  const { onActivate } = props
  const intl = useIntl()

  const isLoyaltyMember = useAppSelector(state => !!state.loyalty.account.data)

  const [loyaltyLandingPageBenefitTiles] = useLoyaltyLandingPageBenefitCopy()

  const largeTiles = useMemo(() => {
    if (!loyaltyLandingPageBenefitTiles) return EmptyArray
    return [
      loyaltyLandingPageBenefitTiles.airport_transfer,
      loyaltyLandingPageBenefitTiles.lounge_pass,
    ].filter(tile => tile?.enabled)
  }, [loyaltyLandingPageBenefitTiles])

  const smallTiles = useMemo(() => {
    if (!loyaltyLandingPageBenefitTiles) return EmptyArray
    return [
      loyaltyLandingPageBenefitTiles.phone_support,
      loyaltyLandingPageBenefitTiles.tour_exp_credit,
      loyaltyLandingPageBenefitTiles.discounted_insurance,
    ].filter(tile => tile?.enabled)
  }, [loyaltyLandingPageBenefitTiles])

  return <Sheet background="light-background" padding="24 0" tabletPadding="48 0">
    <LayoutContainer variant="medium">
      <VerticalSpacer gap={48}>
        <VerticalSpacer gap={8}>
          <Heading variant="heading2" align="center">
            {intl.formatMessage({
              id: 'KQ3OoKliysW-D7PUh4Ss',
              defaultMessage: 'Your Platinum benefits, ready when you are',
              description: 'Heading for the platinum benefits section on the loyalty landing page',
            })}
          </Heading>
          {!isLoyaltyMember && <BodyText variant="large" align="center">
            <FormattedMessage
              id="6g8PJugqFRdUxQ6eoKfh"
              defaultMessage="As one of our valued travellers, you have been fast tracked to Platinum status.{br}Join free today and get instant access to these benefits"
              description="Message for non-members about being fast tracked to Platinum status with call to join"
              values={{ br: <br /> }}
            />
          </BodyText>}
        </VerticalSpacer>
        {loyaltyLandingPageBenefitTiles?.hotel_upgrade?.enabled && <LoyaltyLandingPageLargeTile
          tile={loyaltyLandingPageBenefitTiles.hotel_upgrade}
          onActivate={onActivate}
        />}
        {largeTiles.map((tile, index) => <LoyaltyLandingPageLargeTile
          reverse={index % 2 === 0}
          tile={tile}
          key={index}
        />)}
        <SmallTilesSection>
          {smallTiles.map(tile => <LoyaltyLandingPageSmallTile
            key={tile.image.id}
            tile={tile}
          />)}
        </SmallTilesSection>
        {onActivate && <Typography align="center">
          <TextButton kind="primary" onClick={onActivate}>
            {intl.formatMessage({
              id: 'wqKntdyVSjbKIrCYMkd7',
              defaultMessage: 'Activate your free membership',
              description: 'Button to activate free loyalty membership on the platinum benefits section',
            })}
          </TextButton>
        </Typography>}
      </VerticalSpacer>
    </LayoutContainer>
  </Sheet>
}

export default LoyaltyUserPlatinumBenefits
